import { Download, Eye } from 'lucide-react';
import useAsync from '../../hooks/useAsync';
import documentService from '../../services/documentService';
import { PanelSkeleton } from '../common/LoadingState';
import { formatRelative, pluralize } from '../../utils/format';

const VERB = {
  DOCUMENT_VIEW: 'viewed',
  DOCUMENT_PREVIEW: 'previewed',
  DOCUMENT_DOWNLOAD: 'downloaded',
  DOCUMENT_UPLOAD: 'uploaded',
  DOCUMENT_SHARE: 'shared',
  DOCUMENT_COMMENT_CREATE: 'commented',
  DOCUMENT_COMMENT_UPDATE: 'edited a comment',
  DOCUMENT_COMMENT_DELETE: 'deleted a comment',
  DOCUMENT_COMMENT_RESOLVE: 'resolved a thread',
  DOCUMENT_COMMENT_REOPEN: 'reopened a thread',
  DOCUMENT_SHARE_UPDATE: 'changed sharing',
  DOCUMENT_SHARE_REVOKE: 'removed a share',
  DOCUMENT_RENAME: 'renamed',
  DOCUMENT_UPDATE: 'updated details',
  DOCUMENT_MOVE: 'moved',
  DOCUMENT_COPY: 'copied',
  DOCUMENT_DELETE: 'moved to Trash',
  DOCUMENT_RESTORE: 'restored',
  DOCUMENT_VERSION_CREATE: 'uploaded a new version',
  DOCUMENT_VERSION_RESTORE: 'restored an earlier version',
  EXTERNAL_LINK_CREATE: 'created a secure link',
  EXTERNAL_LINK_ACCESS: 'opened via secure link',
  EXTERNAL_LINK_REVOKE: 'revoked a secure link',
  PERMISSION_DENIED: 'was denied access',
};

/**
 * One number per column, the label under it, and the qualifying detail below.
 *
 * Downloads and views sit together because the pair is the answer: 40 views and no
 * downloads means people looked and moved on, which reads very differently from 40 views
 * and 12 downloads.
 */
function UsageStrip({ stats }) {
  const d = stats.downloads;
  const detail = [
    d.people > 0 && `${pluralize(d.people, 'person', 'people')} internally`,
    d.external > 0 && `${d.external} via secure link`,
  ].filter(Boolean);

  return (
    <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
      <div className="row" style={{ gap: 24 }}>
        {[
          { icon: Download, value: d.total, label: 'download', plural: 'downloads' },
          { icon: Eye, value: stats.views.total, label: 'view', plural: 'views' },
        ].map(({ icon: Icon, value, label, plural }) => (
          <span key={label} className="stack" style={{ gap: 1 }}>
            <span className="row tnum" style={{ gap: 5, fontSize: 19, fontWeight: 650, lineHeight: 1.1 }}>
              <Icon size={13} style={{ color: 'var(--ink-3)' }} />
              {value}
            </span>
            <span className="muted" style={{ fontSize: 11.5 }}>{value === 1 ? label : plural}</span>
          </span>
        ))}
      </div>

      {detail.length > 0 && (
        <div className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>{detail.join(' · ')}</div>
      )}
      {d.lastAt && (
        <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
          {/* No name means it left through a secure link, where there is no account to name. */}
          Last downloaded {formatRelative(d.lastAt)}
          {d.lastBy ? ` by ${d.lastBy}` : ' via a secure link'}
        </div>
      )}
    </div>
  );
}

/** v2 activity list: "User · action" on the left, relative time on the right. */
export default function ActivityFeed({ documentId, limit = 8, framed = true }) {
  const { data: entries, loading } = useAsync(() => documentService.activity(documentId), [documentId]);
  const { data: stats } = useAsync(() => documentService.stats(documentId), [documentId]);
  const rows = (entries || []).slice(0, limit);

  const body = (
    <>
      {stats && <UsageStrip stats={stats} />}
      {loading && !entries && (
        <div style={{ padding: 16 }}>
          <PanelSkeleton rows={3} />
        </div>
      )}
      {!loading && rows.length === 0 && (
        <div className="muted" style={{ padding: '12px 16px', fontSize: 13 }}>
          No activity recorded yet.
        </div>
      )}
      {rows.map((entry) => {
        const denied = entry.result === 'denied';
        return (
          <div key={entry.id} className="side-list__item side-list__item--sm">
            <span className="truncate">
              <strong>{entry.actor?.name || entry.actorName || 'System'}</strong> ·{' '}
              <span style={denied ? { color: 'var(--err-fg)' } : undefined}>
                {denied ? 'was denied access' : VERB[entry.action] || entry.action.toLowerCase().replace(/_/g, ' ')}
              </span>
            </span>
            <span className="muted" style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
              {formatRelative(entry.createdAt)}
            </span>
          </div>
        );
      })}
    </>
  );

  if (!framed) return body;

  return (
    <div className="panel side-list">
      <div className="side-list__head">
        <h3>Activity</h3>
      </div>
      {body}
    </div>
  );
}
